import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
  Input as DlsInput, ComponentLevelNotification,
  Label,
} from '@americanexpress/dls-react';
import { parseHtml, convertFullToHalfWidth } from '../../utils/util';

const getVal = require('lodash/get');

/**
 * returns DLS input component
 * @param {*} props
 */
/* eslint complexity: ["error", 30] -- need to increase complexity */
const Input = (props) => {
  const {
    fieldType,
    fieldLabel,
    fieldPlaceholder,
    fieldId,
    fieldValue,
    fieldValidation,
    filledFormData,
    memberInfo,
    selectKey,
    preFilledWith,
    isPreFilled,
    fetchUrl,
    fileSubmit,
    textWidth,
    isVerifyCard,
    setFormSubmissionPayload,
    formSubmissionPayload,
  } = props;

  const [data, setData] = useState(null);
  const [fieldIdValue, setFieldIdValue] = useState('');
  const [inValidError, setInvalidError] = useState(false);
  const customInvalidMsg = fieldValidation && fieldValidation.customInvalidMsg
    ? fieldValidation.customInvalidMsg : 'Please fill in this field';

  useEffect(() => {
    setFieldIdValue(fieldId);
  }, [fieldId]);

  useEffect(() => {
    if (fieldId !== fieldIdValue) setData('');
    if (filledFormData && filledFormData[fieldId]) {
      setData(filledFormData[fieldId]);
    }
  }, [fieldId]);

  useEffect(() => {
    if (filledFormData && filledFormData[fieldId]) {
      setData(filledFormData[fieldId]);
    }
    if (isPreFilled && preFilledWith === 'filledFormData' && !fetchUrl) {
      if (filledFormData && fieldValidation && fieldValidation.maskedProps) {
        const valueToMask = getVal(filledFormData, fieldValidation.maskedProps.maskFieldId);
        const mask = 'XXXX-XXXXXX-';
        const last5Digits = mask + valueToMask.slice(10, 15);
        setData(last5Digits);
      } else if (filledFormData) {
        setData(getVal(filledFormData, selectKey));
      }
    } else if (isPreFilled && preFilledWith === 'memberInfo' && !fetchUrl) {
      if (memberInfo) {
        setData(getVal(memberInfo, selectKey));
      }
    } else if (fieldValue !== 'default' && fieldValue !== '') {
      setData(fieldValue);
    }
  }, [memberInfo, filledFormData, fieldId]);

  const [errorMsg, setErrorMessage] = useState(null);

  const convertFileSizeToMB = (fileSizeBytes) => fileSizeBytes / (1024 ** 2);
  /**
   * handle input value
   * @param {*} event
   */
  const handleInput = (event) => {
    // handle input type for files
    if (fieldType === 'file') {
      const fileName = event.target.files[0].name;
      const fileSize = event.target.files[0].size;
      // choose regex based on inputType
      let regex;
      if (fieldValidation && fieldValidation.inputType) {
        switch (fieldValidation.inputType) {
          case 'png':
            regex = /(\.png)$/i;
            break;
          default:
            regex = /(\.pdf)$/i;
        }
      }
      // Validate the file with choosen regex
      if (!regex.exec(fileName)) {
        setErrorMessage(fieldValidation.formatErrorMsg);
        fileSubmit(null);
      } else if (convertFileSizeToMB(fileSize) > fieldValidation.fileSizeLimit) {
        setErrorMessage(fieldValidation.sizeErrorMsg);
        fileSubmit(null);
      } else {
        setErrorMessage(null);
        fileSubmit(event.target.files[0]);
      }
    } else {
      setData(event.target.value);
      if (event.target.value === '') setInvalidError(false);
    }
    if (isVerifyCard) {
      if (fieldId === 'cardNumber') {
        const cardNumber = event.target.value;
        setFormSubmissionPayload({
          ...formSubmissionPayload,
          ...{
            elemId: null, cardNumber, cardNameVerify: 1, cardMatchStatus: 1, caseStatusMatch: 1,
          },
        });
      }
      if (fieldId === 'caseNumber') {
        const caseNumber = event.target.value;
        setFormSubmissionPayload({
          ...formSubmissionPayload,
          ...{
            elemId: null, caseNumber, cardNameVerify: 1, cardMatchStatus: 1, caseStatusMatch: 1,
          },
        });
      }
    }
  };

  const handleKeyPress = (event) => {
    if (fieldValidation.isNumeric) {
      // eslint-disable-next-line unicorn/better-regex -- need
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphabet) {
      const re = /^[A-Za-z]+$/;
      if (!re.test(event.key)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphaNumeric) {
      const alphaNu = /^[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}]*$/;
      if (alphaNu.test(event.key)) {
        event.preventDefault();
      }
    }
  };

  const handleValidation = (e) => {
    const str = e.target.value;
    /* eslint-disable-next-line no-useless-escape -- required */
    const re = /^[\d/a-z\-]+$/i;
    if (!re.test(str)) {
      setInvalidError(true);
    } else if (str.length < fieldValidation.minSize) {
      setInvalidError(true);
    } else if (str.length < fieldValidation.maxSize) {
      setInvalidError(true);
    } else {
      setInvalidError(false);
    }
  };

  const inputLabel = (
    <>
      <Label className={fieldLabel.cssProperties.style} htmlFor={fieldId}>
        {fieldLabel.label}
      </Label>
      {fieldValidation && fieldValidation.isToolTip && (
        <>
          <span> </span>
          <Tooltip id="ToolTip">
            <TooltipTrigger>
              <InfoTooltipButton screenReaderLabel="info" />
            </TooltipTrigger>
            <TooltipContent>{fieldValidation.toolTipInfo}</TooltipContent>
          </Tooltip>
        </>
      )}
      {!fieldValidation.required && (
        <span
          className={fieldLabel.cssProperties.style}
        >
          {fieldLabel.optionalLabel ? fieldLabel.optionalLabel : ' (Optional)'}
        </span>
      )}
      {fieldLabel
        && fieldLabel.subLabel
        && (
          <div>
            <Label className={fieldLabel.cssProperties.subLabelStyle} htmlFor={fieldId}>
              {fieldLabel.subLabel}
            </Label>
          </div>
        )}
    </>
  );

  const renderElement = fieldType === 'file' ? (
    <input
      type={fieldType}
      id={fieldId}
      name={fieldId}
      style={{ padding: '1%', borderColor: '#006fcf', color: '#006fcf' }}
      onChange={handleInput}
    />
  ) : (
    <DlsInput
      type={fieldType}
      label={inputLabel}
      id={fieldId}
      key={fieldId}
      name={fieldId}
      className="pad-1-t"
      value={data && textWidth ? convertFullToHalfWidth(data) : data}
      placeholder={fieldPlaceholder}
      onChange={handleInput}
      aria-disabled={fieldValidation.disabled}
      required={fieldValidation.required}
      maxLength={fieldValidation.maxSize}
      minLength={fieldValidation.minSize}
      onKeyPress={handleKeyPress}
      onKeyUp={handleValidation}
      aria-describedby="validFieldAlert"
      title={fieldValidation && fieldValidation.hideTitle ? '' : customInvalidMsg}
      onInvalid={(e) => e.target.setCustomValidity(`${customInvalidMsg}`)}
      onInput={(e) => e.target.setCustomValidity('')}
    />
  );

  return (
    <>
      {renderElement}
      {errorMsg && (
        <ComponentLevelNotification id={`${fieldId}-errorMessage`} className="pad-1-t">
          {parseHtml(errorMsg)}
        </ComponentLevelNotification>
      )}
      {fieldValidation.disabled && (
        <input type="hidden" id={fieldId} name={fieldId} value={data} />
      )}
      {inValidError && fieldValidation?.error && (
        <ComponentLevelNotification id={`${fieldId}-errorMessage`} className="pad-1-t">
          {parseHtml(fieldValidation.error)}
        </ComponentLevelNotification>
      )}
    </>
  );
};

Input.propTypes = {
  fieldType: PropTypes.string,
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldPlaceholder: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValue: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  preFilledWith: PropTypes.string,
  fetchUrl: PropTypes.string,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  selectKey: PropTypes.string,
  isPreFilled: PropTypes.string,
  fileSubmit: PropTypes.func,
  textWidth: PropTypes.string,
  isVerifyCard: PropTypes.bool,
  setFormSubmissionPayload: PropTypes.func,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
};

export default Input;
